import type { WidgetType } from "constants/WidgetConstants";
import * as React from "react";
import type { WidgetProps, WidgetState } from "widgets/BaseWidget";
import BaseWidget from "widgets/BaseWidget";
import BmapComponent from "../component";
import { ValidationTypes } from "constants/WidgetValidation";
import type { Stylesheet } from "entities/AppTheming";
import type { DerivedPropertiesMap } from "utils/WidgetFactory";
import { EvaluationSubstitutionType } from "../../../entities/DataTree/types";
import { PropertyPaneConfig } from "../../../constants/PropertyControlConstants";
import { EventType } from "../../../constants/AppsmithActionConstants/ActionConstants";
import { generateTypeDef, type ExtraDef } from "utils/autocomplete/dataTreeTypeDefCreator";
import _ from "lodash";
import type { AutocompletionDefinitions } from "widgets/constants";
import { getStorage, removeStorage, setStorage } from "utils/localStorage";
import { delayCall, strToJson } from "@byk/utils/Utils";

class BmapViewWidget extends BaseWidget<BmapWidgetProps, WidgetState> {
  locationMarkerKey:any;
  currentLocationKey:any;
  constructor(props: BmapWidgetProps) {
    super(props);
    this.locationMarkerKey=`locationMarkerKey${props.widgetId}`
    this.currentLocationKey=`currentLocationKey${props.widgetId}`
  }

  static getPropertyPaneContentConfig() {
    return [
      {
        sectionName: "标签",
        children: [
          {
            helpText: "Sets the label text of the widget",
            propertyName: "labelText",
            label: "文本",
            controlType: "INPUT_TEXT",
            placeholderText: "Enter label text",
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          }
        ]
      },
      {
        sectionName: '数据',
        children: [
          {
            helpText: "Bind data from an API using {{}}",
            propertyName: "currentData",
            label: "Data",
            controlType: "INPUT_TEXT",
            placeholderText: '[]',
            inputType: "ARRAY",
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.OBJECT_ARRAY,
              params: {
                default: [],
              },
            },
            evaluationSubstitutionType: EvaluationSubstitutionType.SMART_SUBSTITUTE,
          }

        ]
      },
      {
        sectionName: '模板配置',
        children: [
          {
            propertyName: "titleCfg",
            label: "标记title模板",
            controlType: "INPUT_TEXT",
            placeholderText: '',
            inputType: "TEXT",
            isBindProperty: true,
            isTriggerProperty: false,
          },
          {
            propertyName: "contentCfg",
            label: "标记详情模板",
            controlType: "INPUT_TEXT",
            placeholderText: '',
            inputType: "TEXT",
            isBindProperty: true,
            isTriggerProperty: false,
          }
        ]
      },
      {
        sectionName: '中心点',
        children: [
          {
            helpText: "Bind data from an API using {{}}",
            propertyName: "centerLnglat",
            label: "经纬度",
            controlType: "INPUT_TEXT",
            placeholderText: "[{'lng': 114.407936,'lat': 30.478535}]",
            inputType: "JSON",
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.ARRAY,
              params: {
                default: [],
              },
            },
          }
        ]
      },
      {
        sectionName: "设置",
        children: [
          {
            propertyName: "dragEnable",
            label: "是否支持拖动地图",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "zoomEnable",
            label: "是否支持缩放地图",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },

          {
            propertyName: "enableDoubleClickZoom",
            label: "是否开启双击鼠标缩放",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "enableRotate",
            label: "是否开启地图旋转功能",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "enableTilt",
            label: "是否开启地图倾斜功能",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "isSetCenter",
            label: "点击标记是否设为中心",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "isSearch",
            label: "是否允许搜索",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "enableChoosePoint",
            label: "是否开启选点模式",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "isShowCard",
            label: "点击标记点打开记录详情卡片",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "enableDistance",
            label: "是否计算标记点到当前位置距离",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "isEnableMarkerRightClick",
            label: "是否启用标记点右键点击",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "isShowPolyline",
            label: "是否显示轨迹",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "isShowDrawingManager",
            label: "是否绘制工具",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "zoomLevel",
            label: "默认地图层级",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 3,
            max: 20,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            }
          },
          {
            propertyName: "rotation",
            label: "默认地图旋转角度",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            defaultValue: 0,
            min: 0,
            max: 360,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            }
          },
          {
            propertyName: "pitch",
            label: "倾斜度",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 0,
            max: 360,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            }
          },
          {
            propertyName: "coverageRadius",
            label: "显示范围半径(米)",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 0,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            }
          },
        ]
      },
      {
        sectionName: "一般",
        children: [
          {
            helpText: "Controls the visibility of the widget",
            propertyName: "isVisible",
            label: "是否可见",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          }
        ],
      }
    ];
  }
  static getPropertyPaneStyleConfig() {
    return [
      {
        sectionName: "颜色",
        children: [

          {
            propertyName: "borderColor",
            label: "边框颜色",
            helpText: "Control the color of the label associated",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
        ],
      },
      {
        sectionName: "详情设置",
        children: [
          {

            propertyName: "InfoWindowWidth",
            label: "详情窗口宽度",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 1,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
          },
        ],
      },
      {
        sectionName: "边框&阴影",
        children: [
          {

            propertyName: "borderWidth",
            label: "边框宽度",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 0,
            max:10,
            defaultValue: 1,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
          },
          {
            propertyName: "borderRadius",
            label: "边框圆角",
            helpText: "Rounds the corners of the icon button's outer border edge",
            controlType: "BORDER_RADIUS_OPTIONS",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
          {
            propertyName: "boxShadow",
            label: "阴影",
            helpText:
              "Enables you to cast a drop shadow from the frame of the widget",
            controlType: "BOX_SHADOW_OPTIONS",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
        ],
      },
      {
        sectionName: "标记选择样式",
        children: [
          {
              propertyName: "chooseMarkerColor",
              label: "选中样式",
              helpText: "Controls the font family being used",
              controlType: "DROP_DOWN",
              options: [
                {
                  label: "蓝色",
                  value: "loc_blue",
                },
                {
                  label: "红色",
                  value: "loc_red",
                },

              ],
              isJSConvertible: true,
              isBindProperty: true,
              isTriggerProperty: false,
              validation: {
                type: ValidationTypes.TEXT,
              },
          },
          {
              propertyName: "noChooseMarkerColor",
              label: "未选中样式",
              helpText: "Controls the font family being used",
              controlType: "DROP_DOWN",
              options: [
                {
                  label: "蓝色",
                  value: "loc_blue",
                },
                {
                  label: "红色",
                  value: "loc_red",
                },
              ],
              isJSConvertible: true,
              isBindProperty: true,
              isTriggerProperty: false,
              validation: {
                type: ValidationTypes.TEXT,
              },
          },
        ],
      },
      {
        sectionName: "icon图标设置",
        children: [
          {
            propertyName: "iconWidth",
            label: "icon宽度",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 1,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
          },
          {

            propertyName: "iconHeight",
            label: "icon高度",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 1,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            },
          },
        ],
      }
    ];
  }

  static getPropertyPaneEventConfig(): PropertyPaneConfig[] {
    return super.getWidgetEvents('BmapViewWidget');
  }

  static getDerivedPropertiesMap(): DerivedPropertiesMap {
    return {
      value: `{{ this.value }}`,
      getValue: `{{ this.value }}`,
      selectMarkers:`{{ this.selectMarkers }}`,
      chooseAddress:`{{ this.chooseAddress }}`,
      currentLocation:`{{ this.currentLocation }}`,
    };
  }

  static getDefaultPropertiesMap(): Record<string, any> {
    return {
      value: "defaultValue",
      selectMarkers: [],
      chooseAddress: {},
    };
  }

  static getAutocompleteDefinitions(): AutocompletionDefinitions {
      return (widget: any, extraDefsToDefine?: ExtraDef) => {
        const config = {
          isShowSelect: "boolean",
          selectMarkers:"array"
        };
        return config;
      };
  }
  // TODO Find a way to enforce this, (dont let it be set)
  static getMetaPropertiesMap(): Record<string, any> {
    return {
      value: "",
      selectMarkers: [],
      chooseAddress: {},
      currentLocation:{}
    };
  }

  static getStylesheetConfig(): Stylesheet {
    return {
      accentColor: "{{appsmith.theme.colors.primaryColor}}",
      borderRadius: "{{appsmith.theme.borderRadius.appBorderRadius}}",
      boxShadow: "{{appsmith.theme.boxShadow.appBoxShadow}}",
    };
  }
//
  onMapClick = (address:any) => {
    const {pushBatchMetaUpdates, commitBatchMetaUpdates} = this.props;
    pushBatchMetaUpdates('chooseAddress', address);
    commitBatchMetaUpdates();
    super.executeAction({
      triggerPropertyName: "onMapClick",
      dynamicString: this.props.onMapClick,
      event: {
        type: EventType.ON_MAP_CLICK,
      },
    });
  };
  onMarkerClick = () => {
    super.executeAction({
      triggerPropertyName: "onMarkerClick",
      dynamicString: this.props.onMarkerClick,
      event: {
        type: EventType.ON_MARKER_CLICK ,
      },
    });
  };

  onDataChange=(markers:any,noSelectMarkers:any)=>{
    const {pushBatchMetaUpdates, commitBatchMetaUpdates} = this.props;
    pushBatchMetaUpdates('selectCount', markers.length);
    pushBatchMetaUpdates('noSelectCount', noSelectMarkers.length);
    commitBatchMetaUpdates();
  }
  onMarkerRightClick = (markers:any,noSelectMarkers:any) => {
    const {pushBatchMetaUpdates, commitBatchMetaUpdates} = this.props;
    pushBatchMetaUpdates('selectMarkers', markers);
    pushBatchMetaUpdates('noSelectMarkers', noSelectMarkers);
    pushBatchMetaUpdates('selectCount', markers.length);
    pushBatchMetaUpdates('noSelectCount', noSelectMarkers.length);
    const _selectIds=_.map(markers, 'id');
    pushBatchMetaUpdates('selectIds', _selectIds);
    commitBatchMetaUpdates();
  };
  setChoose = () => {
    super.executeAction({
      triggerPropertyName: "setChoose",
      dynamicString: this.props.setChoose,
      event: {
        type: EventType.SET_CHOOSE ,
      },
    });
  };
  setNoChoose = () => {
    super.executeAction({
      triggerPropertyName: "setNoChoose",
      dynamicString: this.props.setNoChoose,
      event: {
        type: EventType.SET_NO_CHOOSE ,
      },
    });
  };
  onSelect = () => {
    const {pushBatchMetaUpdates, commitBatchMetaUpdates} = this.props;
    pushBatchMetaUpdates('isShowSelect', true);
    commitBatchMetaUpdates();
    super.executeAction({
      triggerPropertyName: "onSetSelect",
      dynamicString: this.props.onSetSelect,
      event: {
        type: EventType.ON_SELECT ,
      },
    });
  };
  onUnSelect = () => {
    const {pushBatchMetaUpdates, commitBatchMetaUpdates} = this.props;
    pushBatchMetaUpdates('isShowSelect', false);
    commitBatchMetaUpdates();
    super.executeAction({
      triggerPropertyName: "onSetUnSelect",
      dynamicString: this.props.onSetUnSelect,
      event: {
        type: EventType.ON_UN_SELECT ,
      },
    });
  };
  onCurrentLocationChange=(_currentLocation:any,isflag:false)=>{
    const {pushBatchMetaUpdates, commitBatchMetaUpdates} = this.props;
    pushBatchMetaUpdates('currentLocation', strToJson(getStorage(this.currentLocationKey)));
    commitBatchMetaUpdates();
    if(isflag){
      super.executeAction({
      triggerPropertyName: "onLoad",
      dynamicString: this.props.onLoad,
      event: {
        type: EventType.ON_LOAD,
      },
    });
    }
  }
  removeMapStorage=()=>{
    setStorage(this.locationMarkerKey,"0");
    setStorage(this.currentLocationKey,"");
  }
 

  componentDidMount() {
    delayCall(this.removeMapStorage,1500,4000)
  };
  //onMapClick  onMarkerClick  onMarkerRightClick  setChoose  setNoChoose  onSelect  onUnSelect
  getPageView() {
    return <BmapComponent
      {...this.props}
      onMarkerRightClick={this.onMarkerRightClick}//onMarkerRightClick
      onDataChange={this.onDataChange}
      onCurrentLocationChange={this.onCurrentLocationChange}
      onMapClick={this.onMapClick}//onMapClick
      locationMarkerKey={this.locationMarkerKey}
      currentLocationKey={this.currentLocationKey}
      />;
  }

  static getWidgetType(): WidgetType {
    return "BMAP_VIEW_WIDGET";
  }
}

export interface BmapWidgetProps extends WidgetProps {
  isLast: boolean,
  isSearch: boolean,
  isMultiple: boolean
}

export default BmapViewWidget;
